import axios from 'axios';

import type {
  NFeFiltros,
  ListarNFesResponse,
  EmissaoNFe,
  CriarRascunhoNFeRequest,
  CriarRascunhoNFeResponse,
  AtualizarCabecalhoRequest,
  AtualizarDestinatarioRequest,
  AdicionarItemRequest,
  AdicionarItemComProdutoRequest,
  AtualizarItemRequest,
  AtualizarTributacaoItemRequest,
  AdicionarDuplicataRequest,
  AtualizarDuplicataRequest,
  AtualizarTransporteRequest,
  AtualizarInformacoesAdicionaisRequest,
  ReferenciaNFe,
  AdicionarReferenciaRequest,
  TransmitirNFeResponse,
  CancelarNFeRequest,
  MunicipioItem,
  BuscarProdutosNFeItem,
  ProdutoFiscalNFeResponse,
  RegraFiscalNFeItem,
} from '../types/nfe';

const BASE_URL = '/api/nfe';

export const buscarMunicipios = async (termo: string, uf?: string): Promise<MunicipioItem[]> => {
  const { data } = await axios.get('/api/municipios', { params: { termo: termo || undefined, uf: uf || undefined } });
  return data.itens ?? [];
};

/**
 * Lista NFes com filtros e paginação
 */
export const listarNFes = async (filtros: NFeFiltros): Promise<ListarNFesResponse> => {
  const params = new URLSearchParams();
  filtros.empresaIds?.forEach(id => params.append('empresaIds', id));
  if (filtros.status != null) params.set('status', filtros.status);
  if (filtros.dataInicio) params.set('dataInicio', filtros.dataInicio);
  if (filtros.dataFim) params.set('dataFim', filtros.dataFim);
  params.set('pageNumber', String(filtros.pageNumber));
  params.set('pageSize', String(filtros.pageSize));
  const { data } = await axios.get<ListarNFesResponse>(BASE_URL, { params });
  return data;
};

/**
 * Cria um novo rascunho de NFe
 */
export const criarRascunhoNFe = async (request: CriarRascunhoNFeRequest): Promise<CriarRascunhoNFeResponse> => {
  const { data } = await axios.post<CriarRascunhoNFeResponse>(BASE_URL, request);
  return data;
};

/**
 * Obtém detalhes completos de uma NFe
 */
export const obterNFe = async (id: string): Promise<EmissaoNFe> => {
  const { data } = await axios.get<EmissaoNFe>(`${BASE_URL}/${id}`);
  return data;
};


/**
 * Exclui um rascunho de NFe
 */
export const excluirNFe = async (id: string): Promise<void> => {
  await axios.delete(`${BASE_URL}/${id}`);
};

/**
 * Atualiza cabeçalho/identificação da NFe (natureza, finalidade, tipo, ambiente, indicadores, empresa)
 */
export const atualizarCabecalho = async (
  nfeId: string,
  request: AtualizarCabecalhoRequest
): Promise<void> => {
  await axios.patch(`${BASE_URL}/${nfeId}/cabecalho`, request);
};

/**
 * Atualiza os dados do destinatário
 */
export const atualizarDestinatario = async (
  nfeId: string,
  request: AtualizarDestinatarioRequest
): Promise<void> => {
  await axios.patch(`${BASE_URL}/${nfeId}/destinatario`, request);
};

/**
 * Adiciona um item à NFe
 */
export const adicionarItem = async (
  nfeId: string,
  request: AdicionarItemRequest
): Promise<void> => {
  await axios.post(`${BASE_URL}/${nfeId}/itens`, request);
};

/**
 * Adiciona um item à NFe a partir de um produto (com auto-preenchimento fiscal)
 */
export const adicionarItemComProduto = async (
  nfeId: string,
  request: AdicionarItemComProdutoRequest
): Promise<void> => {
  await axios.post(`${BASE_URL}/${nfeId}/itens`, request);
};

/**
 * Adiciona um item avulso à NFe (sem produto cadastrado)
 */
export const adicionarItemAvulso = async (
  nfeId: string,
  request: AdicionarItemRequest
): Promise<void> => {
  await axios.post(`${BASE_URL}/${nfeId}/itens/avulso`, request);
};

/**
 * Atualiza um item da NFe
 */
export const atualizarItem = async (
  nfeId: string,
  itemId: string,
  request: AtualizarItemRequest
): Promise<void> => {
  await axios.patch(`${BASE_URL}/${nfeId}/itens/${itemId}`, request);
};

/**
 * Remove um item da NFe
 */
export const excluirItem = async (nfeId: string, itemId: string): Promise<void> => {
  await axios.delete(`${BASE_URL}/${nfeId}/itens/${itemId}`);
};

/**
 * Atualiza a tributação de um item (override manual)
 */
export const atualizarTributacaoItem = async (
  nfeId: string,
  itemId: string,
  request: AtualizarTributacaoItemRequest
): Promise<void> => {
  await axios.patch(`${BASE_URL}/${nfeId}/itens/${itemId}/tributacao`, request);
};

/**
 * Adiciona uma duplicata (parcela de pagamento) à NFe
 */
export const adicionarDuplicata = async (
  nfeId: string,
  request: AdicionarDuplicataRequest
): Promise<void> => {
  await axios.post(`${BASE_URL}/${nfeId}/duplicatas`, request);
};

/**
 * Atualiza uma duplicata da NFe
 */
export const atualizarDuplicata = async (
  nfeId: string,
  duplicataId: string,
  request: AtualizarDuplicataRequest
): Promise<void> => {
  await axios.put(`${BASE_URL}/${nfeId}/duplicatas/${duplicataId}`, request);
};

/**
 * Remove uma duplicata da NFe
 */
export const excluirDuplicata = async (nfeId: string, duplicataId: string): Promise<void> => {
  await axios.delete(`${BASE_URL}/${nfeId}/duplicatas/${duplicataId}`);
};

/**
 * Atualiza informações de transporte
 */
export const atualizarTransporte = async (
  nfeId: string,
  request: AtualizarTransporteRequest
): Promise<void> => {
  await axios.patch(`${BASE_URL}/${nfeId}/transporte`, request);
};

/**
 * Atualiza informações adicionais
 */
export const atualizarInformacoesAdicionais = async (
  nfeId: string,
  request: AtualizarInformacoesAdicionaisRequest
): Promise<void> => {
  await axios.patch(`${BASE_URL}/${nfeId}/informacoes-adicionais`, request);
};

/**
 * Adiciona uma referência a outra NFe
 */
export const adicionarReferencia = async (
  nfeId: string,
  request: AdicionarReferenciaRequest
): Promise<ReferenciaNFe> => {
  const { data } = await axios.post<ReferenciaNFe>(`${BASE_URL}/${nfeId}/referencias`, request);
  return data;
};

/**
 * Remove uma referência de outra NFe
 */
export const removerReferencia = async (nfeId: string, refId: string): Promise<void> => {
  await axios.delete(`${BASE_URL}/${nfeId}/referencias/${refId}`);
};

/**
 * Transmite a NFe para a SEFAZ
 */
export const transmitirNFe = async (nfeId: string): Promise<TransmitirNFeResponse> => {
  const { data } = await axios.post<TransmitirNFeResponse>(`${BASE_URL}/${nfeId}/transmitir`);
  return data;
};

/**
 * Cancela uma NFe autorizada
 */
export const cancelarNFe = async (nfeId: string, request: CancelarNFeRequest): Promise<void> => {
  await axios.post(`${BASE_URL}/${nfeId}/cancelar`, request);
};

/**
 * Faz download do XML da NFe
 */
export const downloadXmlNFe = async (nfeId: string): Promise<Blob> => {
  const { data } = await axios.get<Blob>(`${BASE_URL}/${nfeId}/xml`, { responseType: 'blob' });
  return data;
};


export const buscarProdutos = async (filter: string): Promise<BuscarProdutosNFeItem[]> => {
  const { data } = await axios.get<BuscarProdutosNFeItem[]>(`${BASE_URL}/produtos`, { params: { filter } });
  return data;
};

export const obterProdutoFiscal = async (produtoId: string, empresaId: string): Promise<ProdutoFiscalNFeResponse> => {
  const { data } = await axios.get<ProdutoFiscalNFeResponse>(`${BASE_URL}/produtos/${produtoId}/fiscal`, { params: { empresaId } });
  return data;
};

export const buscarRegrasFiscais = async (filter: string): Promise<RegraFiscalNFeItem[]> => {
  const { data } = await axios.get<RegraFiscalNFeItem[]>(`${BASE_URL}/regras-fiscais`, { params: { filter: filter || undefined } });
  return data;
};

export const importarXmlNFe = async (xml: File, empresaId: string): Promise<CriarRascunhoNFeResponse> => {
  const formData = new FormData();
  formData.append('xml', xml);
  formData.append('empresaId', empresaId);
  const { data } = await axios.post<CriarRascunhoNFeResponse>(`${BASE_URL}/importar-xml`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const importarPorChaveNFe = async (chaveAcesso: string, empresaId: string): Promise<CriarRascunhoNFeResponse> => {
  const { data } = await axios.post<CriarRascunhoNFeResponse>(`${BASE_URL}/importar-por-chave`, {
    chaveAcesso,
    empresaId,
  });
  return data;
};

export interface AdicionarReferenciaImportadaResponse {
  chaveReferenciada: string;
  nItensAdicionados: number;
}

export const adicionarReferenciaImportadaXml = async (
  emissaoId: string,
  xml: File
): Promise<AdicionarReferenciaImportadaResponse> => {
  const formData = new FormData();
  formData.append('xml', xml);
  const { data } = await axios.post<AdicionarReferenciaImportadaResponse>(
    `${BASE_URL}/${emissaoId}/referencias/importar-xml`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
};

export const adicionarReferenciaImportadaChave = async (
  emissaoId: string,
  chaveAcesso: string
): Promise<AdicionarReferenciaImportadaResponse> => {
  const { data } = await axios.post<AdicionarReferenciaImportadaResponse>(
    `${BASE_URL}/${emissaoId}/referencias/importar-chave`,
    { chaveAcesso }
  );
  return data;
};

export interface NFeEvento {
  id: string;
  tipoEvento: number;
  nSeqEvento: number;
  justificativa: string | null;
  resultado: string;
  cStat: number | null;
  xMotivo: string | null;
  protocolo: string | null;
  dhRecbto: string | null;
  dhTentativa: string;
}

export interface EmitirCartaCorrecaoRequest {
  correcao: string;
}

export interface EmitirCartaCorrecaoResponse {
  emissaoId: string;
  eventoId: string;
  chaveAcesso: string;
  protocolo: string;
  nSeqEvento: number;
}

export const obterEventosNFe = async (emissaoId: string): Promise<NFeEvento[]> => {
  const { data } = await axios.get<NFeEvento[]>(`${BASE_URL}/${emissaoId}/eventos`);
  return data;
};

export const emitirCartaCorrecaoNFe = async (
  emissaoId: string,
  request: EmitirCartaCorrecaoRequest
): Promise<EmitirCartaCorrecaoResponse> => {
  const { data } = await axios.post<EmitirCartaCorrecaoResponse>(
    `${BASE_URL}/${emissaoId}/carta-correcao`,
    request
  );
  return data;
};

export const downloadXmlEvento = async (emissaoId: string, eventoId: string): Promise<Blob> => {
  const { data } = await axios.get<Blob>(`${BASE_URL}/${emissaoId}/eventos/${eventoId}/xml`, {
    responseType: 'blob',
  });
  return data;
};

export const downloadPdfCCe = async (emissaoId: string, eventoId: string): Promise<Blob> => {
  const { data } = await axios.get<Blob>(`${BASE_URL}/${emissaoId}/eventos/${eventoId}/pdf`, {
    responseType: 'blob',
  });
  return data;
};

export default {
  listarNFes,
  criarRascunhoNFe,
  obterNFe,
  excluirNFe,
  atualizarCabecalho,
  atualizarDestinatario,
  adicionarItem,
  adicionarItemAvulso,
  atualizarItem,
  excluirItem,
  adicionarDuplicata,
  atualizarDuplicata,
  excluirDuplicata,
  atualizarTransporte,
  atualizarInformacoesAdicionais,
  adicionarReferencia,
  removerReferencia,
  transmitirNFe,
  cancelarNFe,
  downloadXmlNFe,
  buscarProdutos,
  obterProdutoFiscal,
  buscarRegrasFiscais,
  importarXmlNFe,
  importarPorChaveNFe,
  adicionarReferenciaImportadaXml,
  adicionarReferenciaImportadaChave,
  obterEventosNFe,
  emitirCartaCorrecaoNFe,
  downloadXmlEvento,
  downloadPdfCCe,
};
